import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import { HttpClient } from '../../../api/httpClient'
import { API_URL } from '../../../api/RestAPI'
import apiHeaders from '../../../api/apiHeaders'
import SectionSpacingFields from './shared/SectionSpacingFields'
import useTranslation from '../../../hooks/useTranslation'
import toast from '../../../utils/toast'

const emptyItem = () => ({
  id: `i_${Math.random().toString(36).slice(2, 10)}`,
  sourceType: 'category',
  sourceId: '',
  sourceName: '',
  sourceSlug: '',
  customTitle: '',
  visible: true,
})

const getSourcePreviewImage = (source) => {
  if (!source) return ''
  // Prefer the full image — thumbnail_image/image_thumbnail are 50x50 derivatives
  // that look blurry stretched across the 120x80 (object-fit: cover) preview.
  return source.image || source.thumbnail_image || source.image_thumbnail || ''
}

export default function CategoryTilesSettings({ settings, onChange }) {
  const { t } = useTranslation()
  const [categories, setCategories] = useState([])
  const [collections, setCollections] = useState([])

  const items = settings.items || []

  useEffect(() => {
    HttpClient.get(`${API_URL}/categories?limit=all`, apiHeaders())
      .then((res) => {
        const cats = res.data?.data || res.data?.categories || []
        setCategories(Array.isArray(cats) ? cats : [])
      })
      .catch(() => toast.error(t('labels.themes.failedToLoadCategories')))

    HttpClient.get(`${API_URL}/collections?limit=all`, apiHeaders())
      .then((res) => {
        const cols = res.data?.data || res.data?.collections || []
        setCollections(Array.isArray(cols) ? cols : [])
      })
      .catch(() => toast.error(t('labels.themes.failedToLoadCollections')))
  }, [])

  const updateItem = (index, patch) => {
    const nextItems = items.map((item, i) => (
      i === index ? { ...item, ...patch } : item
    ))

    onChange({
      ...settings,
      items: nextItems
    })
  }

  const addItem = () => {
    onChange({
      ...settings,
      items: [...items, emptyItem()]
    })
  }

  const removeItem = (index) => {
    onChange({
      ...settings,
      items: items.filter((_, i) => i !== index)
    })
  }

  const handleSourceChange = (index, sourceType, sourceId) => {
    const sourceList = sourceType === 'collection' ? collections : categories
    const selected = sourceList.find((item) => String(item.id) === String(sourceId))
    const currentCustomTitle = items[index]?.customTitle || ''

    updateItem(index, {
      sourceType,
      sourceId,
      sourceName: selected?.name || '',
      sourceSlug: selected?.slug || '',
      customTitle: currentCustomTitle || selected?.name || '',
    })
  }

  return (
    <Stack spacing={2}>
      <SectionSpacingFields
        settings={settings}
        onChange={onChange}
      />

      <TextField
        label={t('labels.themes.sectionTitle')}
        size="small"
        fullWidth
        value={settings.title || ''}
        onChange={(e) => onChange({ ...settings, title: e.target.value })}
      />

      <Stack direction="row" spacing={2}>
        <TextField
          label={t('labels.themes.columns')}
          type="number"
          size="small"
          fullWidth
          inputProps={{ min: 1, max: 4 }}
          value={settings.columns ?? 2}
          onChange={(e) => onChange({
            ...settings,
            columns: parseInt(e.target.value, 10) || 2
          })}
        />

        <TextField
          label={t('labels.themes.cardGap')}
          type="number"
          size="small"
          fullWidth
          inputProps={{ min: 0, max: 60 }}
          value={settings.gap ?? 16}
          onChange={(e) => onChange({
            ...settings,
            gap: parseInt(e.target.value, 10) || 0
          })}
        />
      </Stack>

      <TextField
        label={t('labels.themes.leftRightSpacing')}
        type="number"
        size="small"
        fullWidth
        inputProps={{ min: 0, max: 120 }}
        value={settings.paddingX ?? 0}
        onChange={(e) => onChange({
          ...settings,
          paddingX: parseInt(e.target.value, 10) || 0
        })}
      />

      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle2">
          {t('labels.themes.cardNumber')} ({items.length})
        </Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={addItem}>
          {t('labels.themes.addCard')}
        </Button>
      </Stack>

      {items.map((item, index) => {
        const sourceList = item.sourceType === 'collection' ? collections : categories
        const selected = sourceList.find((source) => String(source.id) === String(item.sourceId))
        const previewImage = getSourcePreviewImage(selected)

        return (
          <Box key={item.id} sx={{ p: 2, border: '1px solid #e5e7eb', borderRadius: 2 }}>
            <Stack spacing={2}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2">
                  {t('labels.themes.cardNumber')} {index + 1}
                </Typography>
                <IconButton size="small" onClick={() => removeItem(index)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
              <Stack direction="row" spacing={2}>
                <FormControl size="small" fullWidth>
                  <InputLabel>{t('labels.themes.sourceType')}</InputLabel>
                  <Select
                    value={item.sourceType || 'category'}
                    label={t('labels.themes.sourceType')}
                    onChange={(e) => handleSourceChange(index, e.target.value, '')}
                  >
                    <MenuItem value="category">{t('labels.themes.category')}</MenuItem>
                    <MenuItem value="collection">{t('labels.themes.collection')}</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel>{item.sourceType === 'collection' ? t('labels.themes.collection') : t('labels.themes.category')}</InputLabel>
                  <Select
                    value={item.sourceId || ''}
                    label={item.sourceType === 'collection' ? t('labels.themes.collection') : t('labels.themes.category')}
                    onChange={(e) => handleSourceChange(index, item.sourceType, e.target.value)}
                  >
                    {sourceList.map((source) => (
                      <MenuItem key={source.id} value={source.id}>
                        {source.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>

              {selected && (
                <Stack spacing={1}>
                  <Typography variant="caption" color="text.secondary">
                    {t('labels.themes.selectedSource')} {selected.name}
                  </Typography>

                  {previewImage ? (
                    <Box
                      sx={{
                        width: 120,
                        height: 80,
                        borderRadius: 1,
                        overflow: 'hidden',
                        border: '1px solid #e5e7eb',
                        backgroundColor: '#f8fafc',
                      }}
                    >
                      <img
                        src={previewImage}
                        alt={selected.name}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block'
                        }}
                      />
                    </Box>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      {t('labels.themes.noImageFoundForSource')} {item.sourceType === 'collection' ? t('labels.themes.collection').toLowerCase() : t('labels.themes.category').toLowerCase()}
                    </Typography>
                  )}
                </Stack>
              )}
              <TextField
                label={t('labels.themes.cardTitle')}
                size="small"
                fullWidth
                value={item.customTitle || item.sourceName || ''}
                onChange={(e) => updateItem(index, { customTitle: e.target.value })}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={item.visible !== false}
                    onChange={(e) => updateItem(index, { visible: e.target.checked })}
                  />
                }
                label={t('labels.themes.visible')}
              />
            </Stack>
          </Box>
        )
      })}
    </Stack>
  )
}